import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Loading, Badge, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ReportCharts from '../components/ReportCharts';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';
import AttendanceReport from '../components/AttendanceReport';
import MonthlyAttendanceHeatmap from '../components/MonthlyAttendanceHeatmap';


const AttendanceReportPage = () => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [filters, setFilters] = useState({ 
    period: 'monthly',
    startDate: '2026-03-01',  // Set default start date
    endDate: '2026-03-31',    // Set default end date
    limit: 10
  });
  
  // Use the useReports hook correctly - it returns an object with data, loading, error, etc.
  const { data, loading, error, refetch } = useReports('attendance', filters);

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('AttendanceReportPage - Full data from API:', data);
    if (data) {
      console.log('AttendanceReportPage - data structure:', data);
      console.log('AttendanceReportPage - data.data:', data.data);
      
      // Set attendance data for the heatmap component
      if (data.data) {
        setAttendanceData(data);
      }
    }
  }, [data]);

  // Remove the duplicate useEffect that was incorrectly calling useReports
  // The useReports hook is already called above, don't call it again inside useEffect

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    // Refetch with new filters
    refetch();
  };

  const handleExport = (format) => {
    //exportUtils.exportData(rawData, format, 'attendance-report');
  };

  // Extract the actual data array from the nested structure
  const getRawDataArray = () => {
    if (!data) return [];
    
    // Based on your API response structure
    if (data.data && data.data.attendances && Array.isArray(data.data.attendances)) {
      return data.data.attendances;
    }
    
    // Alternative structure
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  };

  const rawData = getRawDataArray();

  console.log("Processed attendance data:", attendanceData);
  console.log("Raw data array:", rawData);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    // <MainLayout>
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Attendance Report</h1>
      
      {/* Filters Section */}
      <div className="mb-6">
        <ReportFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Export Buttons */}
      <div className="mb-6">
        <ExportButtons 
          onExport={handleExport}
          disabled={!rawData || rawData.length === 0}
        />
      </div>

      {/* Statistics Cards - Optional */}
      {data?.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <h3 className="text-sm text-gray-500 mb-1">Total Days</h3>
            <p className="text-2xl font-bold">{data.data.total_days || 0}</p>
          </Card>
          <Card className="p-4">
            <h3 className="text-sm text-gray-500 mb-1">Present Days</h3>
            <p className="text-2xl font-bold text-green-600">{data.data.present_days || 0}</p>
          </Card>
          <Card className="p-4">
            <h3 className="text-sm text-gray-500 mb-1">Late Days</h3>
            <p className="text-2xl font-bold text-yellow-600">{data.data.late_days || 0}</p>
          </Card>
          <Card className="p-4">
            <h3 className="text-sm text-gray-500 mb-1">Absent Days</h3>
            <p className="text-2xl font-bold text-red-600">{data.data.absent_days || 0}</p>
          </Card>
        </div>
      )}

      {/* Detailed Attendance Report */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Detailed Attendance Records</h2>
        <AttendanceReport data={data} />
      </div>

      {/* Monthly Heatmap */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Monthly Attendance Heatmap</h2>
        <MonthlyAttendanceHeatmap 
          data={attendanceData}
          year={2026}
          month={2} // 0-based: 0=Jan, 1=Feb, 2=Mar
        />
      </div>

      {/* Report Table - Optional */}
      {rawData && rawData.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Attendance Table View</h2>
          <ReportTable 
            data={rawData}
            columns={[
              { key: 'user.first_name', label: 'First Name' },
              { key: 'user.last_name', label: 'Last Name' },
              { key: 'attendance_date', label: 'Date' },
              { key: 'clock_in', label: 'Clock In' },
              { key: 'clock_out', label: 'Clock Out' },
              { key: 'status', label: 'Status' },
              { key: 'net_working_minutes', label: 'Hours', format: (val) => (val / 60).toFixed(1) }
            ]}
          />
        </div>
      )}

      {/* Report Charts - Optional */}
      {rawData && rawData.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Attendance Charts</h2>
          <ReportCharts data={rawData} type="attendance" />
        </div>
      )}

      {/* Empty State */}
      {(!rawData || rawData.length === 0) && !loading && (
        <Alert 
          type="info" 
          message="No attendance data found for the selected date range. Please adjust your filters." 
        />
      )}
    </div>
    // </MainLayout>
  );
};

export default AttendanceReportPage;